/**
 * Applies a watermark logo to a base image using HTML Canvas.
 * @param {string} baseImageUrl - URL of the main image.
 * @param {string} watermarkImageUrl - URL of the logo image (preferably PNG).
 * @param {object} options - Configuration for watermark placement.
 * @param {'bottom-right'|'bottom-left'|'top-right'|'top-left'|'center'|'tile'} [options.position='bottom-right'] - Watermark position.
 * @param {number} [options.opacity=0.7] - Watermark opacity (0 to 1).
 * @param {number} [options.scale=0.1] - Watermark size relative to base image width (e.g., 0.1 for 10%).
 * @param {number} [options.margin=0.02] - Margin from edge relative to base image width (e.g., 0.02 for 2%).
 * @returns {Promise<Blob>} - A Promise that resolves with the watermarked image as a Blob.
 */
export const applyWatermark = (baseImageUrl, watermarkImageUrl, options = {}) => {
  return new Promise(async (resolve, reject) => {
    const {
      position = 'bottom-right',
      opacity = 0.7,
      scale = 0.1,
      margin = 0.02
    } = options;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const baseImage = new Image();
    const watermarkImage = new Image();

    // Handle CORS for images from different origins (like Supabase Storage)
    baseImage.crossOrigin = "Anonymous";
    watermarkImage.crossOrigin = "Anonymous";

    let baseLoaded = false;
    let watermarkLoaded = false;

    const checkCompletion = () => {
      if (baseLoaded && watermarkLoaded) {
        try {
          // Set canvas size to base image dimensions
          canvas.width = baseImage.naturalWidth;
          canvas.height = baseImage.naturalHeight;

          // Draw base image
          ctx.drawImage(baseImage, 0, 0);

          // Calculate watermark dimensions and position
          const baseWidth = baseImage.naturalWidth;
          const baseHeight = baseImage.naturalHeight;
          const watermarkScale = baseWidth * scale;
          const watermarkAspectRatio = watermarkImage.naturalWidth / watermarkImage.naturalHeight;
          let wmWidth = watermarkScale;
          let wmHeight = watermarkScale / watermarkAspectRatio;

          // Adjust if height becomes the limiting factor
          if (wmHeight > baseHeight * scale) {
             wmHeight = baseHeight * scale;
             wmWidth = wmHeight * watermarkAspectRatio;
          }

          const marginPixels = baseWidth * margin;

          let x, y;

          switch (position) {
            case 'bottom-left':
              x = marginPixels;
              y = baseHeight - wmHeight - marginPixels;
              break;
            case 'top-right':
              x = baseWidth - wmWidth - marginPixels;
              y = marginPixels;
              break;
            case 'top-left':
              x = marginPixels;
              y = marginPixels;
              break;
            case 'center':
              x = (baseWidth - wmWidth) / 2;
              y = (baseHeight - wmHeight) / 2;
              break;
            case 'tile': // Tiling logic (simplified example)
              ctx.globalAlpha = opacity;
              for (let tileY = 0; tileY < baseHeight; tileY += wmHeight + marginPixels * 5) {
                for (let tileX = 0; tileX < baseWidth; tileX += wmWidth + marginPixels * 5) {
                  ctx.drawImage(watermarkImage, tileX, tileY, wmWidth, wmHeight);
                }
              }
              ctx.globalAlpha = 1.0; // Reset alpha
              break;
            case 'bottom-right':
            default:
              x = baseWidth - wmWidth - marginPixels;
              y = baseHeight - wmHeight - marginPixels;
              break;
          }

          // Draw watermark (if not tiled)
          if (position !== 'tile') {
            ctx.globalAlpha = opacity;
            ctx.drawImage(watermarkImage, x, y, wmWidth, wmHeight);
            ctx.globalAlpha = 1.0; // Reset alpha
          }

          // Convert canvas to Blob
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create Blob from canvas'));
            }
          }, 'image/png'); // Always output as PNG to preserve transparency

        } catch (drawError) {
          console.error("Error drawing watermark:", drawError);
          reject(new Error('Failed to apply watermark'));
        }
      }
    };

    baseImage.onload = () => { baseLoaded = true; checkCompletion(); };
    watermarkImage.onload = () => { watermarkLoaded = true; checkCompletion(); };
    baseImage.onerror = (e) => reject(new Error(`Failed to load base image: ${baseImageUrl} ${e.message}`));
    watermarkImage.onerror = (e) => reject(new Error(`Failed to load watermark image: ${watermarkImageUrl} ${e.message}`));

    // Add cache-busting query param to try and force fresh load if CORS issues persist
    const cacheBuster = `?t=${Date.now()}`;
    baseImage.src = baseImageUrl.includes('?') ? `${baseImageUrl}&${cacheBuster}` : `${baseImageUrl}${cacheBuster}`;
    watermarkImage.src = watermarkImageUrl.includes('?') ? `${watermarkImageUrl}&${cacheBuster}` : `${watermarkImageUrl}${cacheBuster}`;
  });
};
